import { useEffect, useState } from "react";
import { BiSearch, BiEdit } from "react-icons/bi";
import {
    addCountry,
    addDistrict,
    addState,
    addTaluka,
    getAllCountries,
    getDistrictsByState,
    getStatesByCountry,
    getTalukasByDistrict,
    updateCountry,
    updateDistrict,
    updateState,
    updateTaluka
} from "../../services/LocationService";

const AdminLocationPage = () => {
    /* hierarchical selections */
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedState, setSelectedState] = useState(null);
    const [selectedDistrict, setSelectedDistrict] = useState(null);
    const [selectedTaluka, setSelectedTaluka] = useState(null);

    /* selections for editing/updating */
    const [editCountry, setEditCountry] = useState(null);
    const [editState, setEditState] = useState(null);
    const [editDistrict, setEditDistrict] = useState(null);
    const [editTaluka, setEditTaluka] = useState(null);

    /* lists */
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [talukas, setTalukas] = useState([]);

    /* inputs */
    const [countryName, setCountryName] = useState("");
    const [stateName, setStateName] = useState("");
    const [districtName, setDistrictName] = useState("");
    const [talukaName, setTalukaName] = useState("");

    /* search queries */
    const [searchCountry, setSearchCountry] = useState("");
    const [searchState, setSearchState] = useState("");
    const [searchDistrict, setSearchDistrict] = useState("");
    const [searchTaluka, setSearchTaluka] = useState("");

    /* loading states */
    const [loadingCountries, setLoadingCountries] = useState(false);
    const [loadingStates, setLoadingStates] = useState(false);
    const [loadingDistricts, setLoadingDistricts] = useState(false);
    const [loadingTalukas, setLoadingTalukas] = useState(false);

    /* ---------------- FETCHERS ---------------- */
    const loadCountries = async () => {
        setLoadingCountries(true);
        try {
            const res = await getAllCountries();
            setCountries(res || []);
        } catch (error) {
            setCountries([]);
        } finally {
            setLoadingCountries(false);
        }
    };

    const loadStates = async () => {
        setLoadingStates(true);
        try {
            const res = await getStatesByCountry(selectedCountry.id);
            setStates(res || []);
        } catch (error) {
            setStates([]);
        } finally {
            setLoadingStates(false);
        }
    };

    const loadDistricts = async () => {
        setLoadingDistricts(true);
        try {
            const res = await getDistrictsByState(selectedState.id);
            setDistricts(res || []);
        } catch (error) {
            setDistricts([]);
        } finally {
            setLoadingDistricts(false);
        }
    };

    const loadTalukas = async () => {
        setLoadingTalukas(true);
        try {
            const res = await getTalukasByDistrict(selectedDistrict.id);
            setTalukas(res || []);
        } catch (error) {
            setTalukas([]);
        } finally {
            setLoadingTalukas(false);
        }
    };

    /* ---------------- EFFECTS ---------------- */
    useEffect(() => {
        loadCountries();
    }, []);

    useEffect(() => {
        if (!selectedCountry) {
            setStates([]);
            return;
        }
        loadStates();
    }, [selectedCountry]);

    useEffect(() => {
        if (!selectedState) {
            setDistricts([]);
            return;
        }
        loadDistricts();
    }, [selectedState]);

    useEffect(() => {
        if (!selectedDistrict) {
            setTalukas([]);
            return;
        }
        loadTalukas();
    }, [selectedDistrict]);

    /* ---------------- SAVE HANDLERS ---------------- */
    const handleSaveCountry = async (e) => {
        e.preventDefault();
        if (!countryName.trim()) return;
        try {
            if (editCountry) await updateCountry(editCountry.id, { name: countryName });
            else await addCountry({ name: countryName });
            setCountryName("");
            setEditCountry(null);
            loadCountries();
        } catch (err) { alert("Failed to save country"); }
    };

    const handleSaveState = async (e) => {
        e.preventDefault();
        if (!stateName.trim() || !selectedCountry) return;
        try {
            if (editState) await updateState(editState.id, { name: stateName });
            else await addState({ name: stateName, countryId: selectedCountry.id });
            setStateName("");
            setEditState(null);
            loadStates();
        } catch (err) { alert("Failed to save state"); }
    };

    const handleSaveDistrict = async (e) => {
        e.preventDefault();
        if (!districtName.trim() || !selectedState) return;
        try {
            if (editDistrict) await updateDistrict(editDistrict.id, { name: districtName });
            else await addDistrict({ name: districtName, stateId: selectedState.id });
            setDistrictName("");
            setEditDistrict(null);
            loadDistricts();
        } catch (err) { alert("Failed to save district"); }
    };

    const handleSaveTaluka = async (e) => {
        e.preventDefault();
        if (!talukaName.trim() || !selectedDistrict) return;
        try {
            if (editTaluka) await updateTaluka(editTaluka.id, { name: talukaName });
            else await addTaluka({ name: talukaName, districtId: selectedDistrict.id });
            setTalukaName("");
            setEditTaluka(null);
            loadTalukas();
        } catch (err) { alert("Failed to save taluka"); }
    };

    /* ---------------- FILTER LISTS ---------------- */
    const filteredCountries = countries.filter(c => c.name.toLowerCase().includes(searchCountry.toLowerCase()));
    const filteredStates = states.filter(s => s.name.toLowerCase().includes(searchState.toLowerCase()));
    const filteredDistricts = districts.filter(d => d.name.toLowerCase().includes(searchDistrict.toLowerCase()));
    const filteredTalukas = talukas.filter(t => t.name.toLowerCase().includes(searchTaluka.toLowerCase()));

    /* ---------------- COLUMN CONFIGS ---------------- */
    const columns = [
        {
            title: "Country",
            items: filteredCountries,
            loading: loadingCountries,
            value: countryName,
            setValue: setCountryName,
            searchValue: searchCountry,
            setSearchValue: setSearchCountry,
            onSave: handleSaveCountry,
            editItem: editCountry,
            setEditItem: (item) => { setEditCountry(item); setCountryName(item ? item.name : ""); },
            selectedItem: selectedCountry,
            onSelect: (item) => { setSelectedCountry(item); setSelectedState(null); setSelectedDistrict(null); setSelectedTaluka(null); },
            dependencyMissingMsg: null,
            parentName: null
        },
        {
            title: "State",
            items: filteredStates,
            loading: loadingStates,
            value: stateName,
            setValue: setStateName,
            searchValue: searchState,
            setSearchValue: setSearchState,
            onSave: handleSaveState,
            editItem: editState,
            setEditItem: (item) => { setEditState(item); setStateName(item ? item.name : ""); },
            selectedItem: selectedState,
            onSelect: (item) => { setSelectedState(item); setSelectedDistrict(null); setSelectedTaluka(null); },
            dependencyMissingMsg: !selectedCountry ? "Select a Country to view States" : null,
            parentName: selectedCountry?.name
        },
        {
            title: "District",
            items: filteredDistricts,
            loading: loadingDistricts,
            value: districtName,
            setValue: setDistrictName,
            searchValue: searchDistrict,
            setSearchValue: setSearchDistrict,
            onSave: handleSaveDistrict,
            editItem: editDistrict,
            setEditItem: (item) => { setEditDistrict(item); setDistrictName(item ? item.name : ""); },
            selectedItem: selectedDistrict,
            onSelect: (item) => { setSelectedDistrict(item); setSelectedTaluka(null); },
            dependencyMissingMsg: !selectedState ? "Select a State to view Districts" : null,
            parentName: selectedState?.name
        },
        {
            title: "Taluka",
            items: filteredTalukas,
            loading: loadingTalukas,
            value: talukaName,
            setValue: setTalukaName,
            searchValue: searchTaluka,
            setSearchValue: setSearchTaluka,
            onSave: handleSaveTaluka,
            editItem: editTaluka,
            setEditItem: (item) => { setEditTaluka(item); setTalukaName(item ? item.name : ""); },
            selectedItem: selectedTaluka,
            onSelect: (item) => { setSelectedTaluka(item); },
            dependencyMissingMsg: !selectedDistrict ? "Select a District to view Talukas" : null,
            parentName: selectedDistrict?.name
        }
    ];

    return (
        <div className="p-4 space-y-6">
            <h2 className="admin-heading text-[#3f4db3]">Location Master</h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6">
                {columns.map((col, index) => (
                    <div key={index} className="bg-white rounded-xl shadow-md border border-gray-100 flex flex-col h-[400px] overflow-hidden">
                        
                        {/* Header */}
                        <div className="p-3 bg-[#3f4db3] text-white flex justify-between items-center shadow-sm">
                            <h3 className="font-semibold text-white">{col.title}</h3>
                            {col.parentName && <span className="text-xs bg-white/20 px-2 py-0.5 rounded truncate max-w-[120px]">{col.parentName}</span>}
                        </div>

                        {col.dependencyMissingMsg ? (
                            <div className="flex-1 flex items-center justify-center text-sm text-gray-400 p-6 text-center bg-gray-50/50">
                                {col.dependencyMissingMsg}
                            </div>
                        ) : (
                            <>
                                {/* Add / Edit Form */}
                                <form onSubmit={col.onSave} className="p-3 border-b border-gray-200 bg-gray-50 flex gap-2">
                                    <input
                                        type="text"
                                        value={col.value}
                                        onChange={(e) => col.setValue(e.target.value)}
                                        placeholder={`Enter ${col.title}...`}
                                        className="flex-1 border border-gray-300 rounded px-2.5 py-1.5 text-sm focus:outline-[#3f4db3] focus:ring-1 focus:ring-[#3f4db3]"
                                    />
                                    <button
                                        type="submit"
                                        disabled={!col.value.trim()}
                                        className="bg-[#3f4db3] text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-[#2f3a8c] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                                    >
                                        {col.editItem ? "Update" : "Add"}
                                    </button>
                                    {col.editItem && (
                                        <button
                                            type="button"
                                            onClick={() => col.setEditItem(null)}
                                            className="bg-gray-400 text-white px-2 py-1.5 rounded text-sm hover:bg-gray-500 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                    )}
                                </form>

                                {/* Search Bar */}
                                <div className="p-3 border-b border-gray-100 relative">
                                    <BiSearch className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                    <input
                                        type="text"
                                        value={col.searchValue}
                                        onChange={(e) => col.setSearchValue(e.target.value)}
                                        placeholder={`Search...`}
                                        className="w-full border border-gray-200 rounded-full pl-9 pr-4 py-1.5 text-sm bg-gray-50 focus:bg-white focus:border-[#3f4db3] focus:outline-none transition-colors"
                                    />
                                </div>

                                {/* Scrollable List */}
                                <div className="flex-1 overflow-y-auto p-2 bg-gray-50/30">
                                    {col.loading ? (
                                        <div className="flex items-center justify-center h-32 text-sm text-gray-500">Loading...</div>
                                    ) : col.items.length === 0 ? (
                                        <div className="flex items-center justify-center h-32 text-sm text-gray-400">No {col.title}s found</div>
                                    ) : (
                                        <ul className="space-y-1.5">
                                            {col.items.map(item => (
                                                <li
                                                    key={item.id}
                                                    onClick={() => col.onSelect && col.onSelect(item)}
                                                    className={`
                                                        group flex justify-between items-center p-2.5 rounded-lg cursor-pointer text-sm transition-all duration-200 border
                                                        ${col.selectedItem?.id === item.id
                                                            ? "bg-blue-50 border-blue-300 shadow-sm"
                                                            : "bg-white border-transparent hover:border-gray-200 shadow-sm hover:shadow"}
                                                    `}
                                                >
                                                    <span className={`truncate pr-2 select-none ${col.selectedItem?.id === item.id ? "font-semibold text-[#3f4db3]" : "text-gray-700 font-medium"}`}>
                                                        {item.name}
                                                    </span>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); col.setEditItem(item); }}
                                                        className="text-gray-400 hover:text-[#3f4db3] p-1.5 rounded-md hover:bg-blue-100 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
                                                        title="Edit"
                                                    >
                                                        <BiEdit size={16} />
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AdminLocationPage;
